<script>

import * as turf from '@turf/turf'
import mapboxgl from 'mapbox-gl';
import _ from 'lodash';
import {axios} from '../../mixins';

// Import plugins
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import SplitPolygonMode, {
  drawStyles as splitPolygonDrawStyles,
} from "mapbox-gl-draw-split-polygon-mode/dist/index.js";

// Import styles
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import 'mapbox-gl/dist/mapbox-gl.css'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import MapBoxGl from "../../../../nova-components/Geometry/resources/js/components/MapBoxGl.vue";

export default {
  name: 'MapBox',

  mixins: [
    axios
  ],

  props: {
    source: {
      required: false,
      type: Object,
      default: function () {
        return null;
      },
    },
    sourceName: {
      required: false,
      type: String,
      default: 'Field'
    },
    draw: {
      required: true,
      type: Boolean,
    },
    token: {
      required: true,
      type: String,
    },
    esriToken: {
      required: true,
      type: String,
    },
    render: {
      required: false,
      default: true,
      type: Boolean,
    },
    popups: {
      required: false,
      type: Boolean,
      default: false,
    },
    mapId: {
      required: false,
      type: String,
      default: null,
    },
    layer: {
      required: false,
      type: Object,
      default: null,
    },
    farmAddress: {
      required: false,
      type: String,
    },
    zipCode: {
      required: false,
      type: String,
    },
    // Optional height class
    height: {
      required: false,
      type: String
    },
    outline: {
      required: false,
      type: Boolean,
      default: false,
    },
    farmId: {
      required: false,
      type: Number,
      default: null
    },
    toolbarHelpSections: {
      required: false,
      type: Array,
      default: () => [
        {
          title: 'Split Polygon',
          icon: 'fas fa-cut',
          items: [
            'Uncombine features before splitting.',
            'Draw a split line with at least 3 points.',
            'Place at least 1 point inside the polygon being split.',
            'Works best on the first cut.',
          ],
        },
        {
          title: 'Multi-Select Vertices',
          icon: 'fas fa-mouse-pointer',
          items: [
            'Uncombine features before selecting vertices.',
            'Select a polygon before entering Select Vertices mode.',
            'Click and drag a box to select vertices from that polygon.',
          ],
        },
      ],
    },
    helpTitle: {
      required: false,
      type: String,
      default: 'Boundary Editing Help',
    },
  },

  data() {
    return {
      mapStyle: 'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/arcgis/imagery',
      map: null,
      geography: null,
      mapDraw: {},
      loaded: false,
      center: [-93.627594, 42.025494],
      identifier: 'map-' + Math.floor(10000 * Math.random()),
      fullBounds: new mapboxgl.LngLatBounds(),
      sources: [],
      showSettings: false,
      naipLayers: [],
      rasterLayer: '',
      showOtherBoundaries: true,

      // Phase 1 vertex editing additions
      multiVertexMode: false,
      currentEditableFeatureId: null,
      selectedVertexPaths: [],
      selectionBox: {
        active: false,
        start: null,
        current: null,
      },
      suppressNextMapClick: false,
      draggingSelectedVertices: false,
      dragFeatureId: null,
      dragLastLngLat: null,

      // Toolbar help + tooltip UI
      showToolbarHelp: false,
      tooltip: {
        visible: false,
        text: '',
        x: 0,
        y: 0,
      },

      showZoomTooltip: false,
      toolbarNotice: '',
      currentDrawMode: '',
      toolbarExpanded: false,

      sessionToken: '',
      sessionEnd: 0,
    };
  },

  watch: {
    source(newVal) {
      this.processGeography(newVal)
      if (this.map === null) {
        this.renderMap()
      } else {
        if (this.loaded) {
          this.clearMap()
          this.postMapRender()
        }
      }
    },
  },
  created() {
    this.processGeography(this.source)
    this.getNaipLayers()
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onGlobalKeyDown, true);
  },
  mounted() {
    this.renderMap();
    window.addEventListener('keydown', this.onGlobalKeyDown, true);
  },

  methods: {
    /**
     * Initiates creating the map and loading the data.
     */
    async renderMap() {
      await this.createMap();
      this.addControls();

      this.map.on('load', function () {
        let map = this.map;

        map.addSource('satellite-mapbox', {
          'type':'raster',
          'tiles':[
            'https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90'
          ],
          'tileSize': 256
        });
        map.addLayer(
            {
              'id': 'satellite-mapbox',
              'type': 'raster',
              'source': 'satellite-mapbox',
            },
            'Railroad/casing' // Arrange our new layer beneath labels and above roads
        );

        map.addSource('naip-usda',{
          'type':'raster',
          'tiles':[
            'https://gis.apfo.usda.gov/arcgis/rest/services/NAIP/USDA_CONUS_PRIME/ImageServer/tile/{z}/{y}/{x}'
          ],
          'tileSize': 256
        });
        map.addLayer(
            {
              'id': 'naip-usda',
              'type': 'raster',
              'source': 'naip-usda',
              'minzoom': 13,
              'layout': {
                'visibility': 'none',
              }
            },
            'Railroad/casing' // Arrange our new layer beneath labels and above roads
        );

        map.addSource('naip-usgs',{
          'type':'raster',
          'tiles':[
            'https://imagery.nationalmap.gov:443/arcgis/services/USGSNAIPImagery/ImageServer/WMSServer?crs=EPSG:3857&transparency=false&request=GetMap%26version=1.3.0%26format=image/jpeg%26layers=USGSNAIPImagery:NaturalColor&bbox={bbox-epsg-3857}&width=' + map.getCanvas().width + '&height=' + map.getCanvas().height
          ],
          'tileSize': 256
        });
        map.addLayer(
            {
              'id': 'naip-usgs',
              'type': 'raster',
              'source': 'naip-usgs',
              'minzoom': 13,
              'layout': {
                'visibility': 'none',
              }
            },
            'Railroad/casing' // Arrange our new layer beneath labels and above roads
        );

        map.addLayer({
          'id': 'fieldBoundary',
          'type': 'fill',
          'source': {
            'type': 'geojson',
            'data': this.layer
          },
          'layout': {},
          'paint': {
            'fill-color': '#008ED3',
            'fill-opacity': 0.5
          }
        });

        // Remove and replace for readability
        map.removeLayer('10df2279f9684e4a9f6a7f08febac2a9')
        map.addLayer(
            {
              'id': 'satellite-esri',
              'type': 'raster',
              'source': '10df2279f9684e4a9f6a7f08febac2a9',
            },
            'Railroad/casing' // Arrange our new layer beneath labels and above roads
        );

        this.loaded = true
        this.map.resize()
        this.postMapRender()
        this.switchLayers()
        this.ensureSelectedVertexOverlay();
        this.stripMapboxControlTitles();
        let confirmButton = document.getElementById('confirmSelection');
        if(confirmButton) {
          confirmButton.onclick = function () {
            map.resize();
          }
        }
      }.bind(this));
    },

    /**
     * Creates map with added plugins.
     */
    async createMap() {
      mapboxgl.accessToken = this.token;

      if (!localStorage.getItem('sessionEnd')) {
        localStorage.setItem('sessionEnd', 0)
      }

      if (localStorage.getItem('sessionEnd') < Date.now()) {
        const response = await this.get("https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/sessions/start" +
            "?styleFamily=arcgis" +
            "&f=json" +
            "&token=" + this.esriToken)

        localStorage.setItem('sessionToken', response.data.sessionToken)
        localStorage.setItem('sessionEnd', response.data.endTime)
      }

      this.map = new mapboxgl.Map({
        attributionControl: false,
        container: this.mapId ?? this.identifier,
        style: this.mapStyle + '?token=' + localStorage.getItem('sessionToken'), // stylesheet location
        center: this.center,
        zoom: 3, // starting zoom
        // transformRequest: (url, resourceType) => {
        //   return {
        //     url: url,
        //     headers: { 'Access-Control-Allow-Origin': true }
        //   }
        // }
      });

      if (this.draw) {
        const NewSimpleSelect = _.extend(MapboxDraw.modes.simple_select, {
          dragMove() {}
        });

        const vm = this;

        const NewDirectSelect = _.extend({}, MapboxDraw.modes.direct_select, {
          dragFeature() {},

          onDrag(state, e) {
            if (vm.multiVertexMode) {
              return;
            }

            return MapboxDraw.modes.direct_select.onDrag.call(this, state, e);
          },
        });

        // Styles fix since mapbox broke their own
        // Courtesy of https://github.com/mapbox/mapbox-gl-draw/issues/1357#issuecomment-2838175191
        // Leaving out as we can use 1.4.3, but including this for future reference
        const styles=[{id:"gl-draw-polygon-fill",type:"fill",filter:["all",["==","$type","Polygon"],],paint:{"fill-color":["case",["==",["get","active"],"true"],"orange","blue",],"fill-opacity":.1}},{id:"gl-draw-lines",type:"line",filter:["any",["==","$type","LineString"],["==","$type","Polygon"],],layout:{"line-cap":"round","line-join":"round"},paint:{"line-color":["case",["==",["get","active"],"true"],"orange","blue",],"line-dasharray":["case",["==",["get","active"],"true"],["literal",[.2,2]],["literal",[.2,2]],],"line-width":2}},{id:"gl-draw-point-outer",type:"circle",filter:["all",["==","$type","Point"],["==","meta","feature"],],paint:{"circle-radius":["case",["==",["get","active"],"true"],7,5,],"circle-color":"white"}},{id:"gl-draw-point-inner",type:"circle",filter:["all",["==","$type","Point"],["==","meta","feature"],],paint:{"circle-radius":["case",["==",["get","active"],"true"],5,3,],"circle-color":["case",["==",["get","active"],"true"],"orange","blue",]}},{id:"gl-draw-vertex-outer",type:"circle",filter:["all",["==","$type","Point"],["==","meta","vertex"],["!=","mode","simple_select"],],paint:{"circle-radius":["case",["==",["get","active"],"true"],7,5,],"circle-color":"white"}},{id:"gl-draw-vertex-inner",type:"circle",filter:["all",["==","$type","Point"],["==","meta","vertex"],["!=","mode","simple_select"],],paint:{"circle-radius":["case",["==",["get","active"],"true"],5,3,],"circle-color":"orange"}},{id:"gl-draw-midpoint",type:"circle",filter:["all",["==","meta","midpoint"],],paint:{"circle-radius":3,"circle-color":"orange"}},];

        const splitModes = SplitPolygonMode(MapboxDraw.modes);

        this.mapDraw = new MapboxDraw({
          displayControlsDefault: false,
          controls: {},
          modes: {
            ...splitModes,
            simple_select: NewSimpleSelect,
            direct_select: NewDirectSelect,
          },
          styles: [...splitPolygonDrawStyles(styles)],
          userProperties: true,
        });

        this.map.on('draw.create', (e) => {
          this.broadcastUpdate()
        });
        this.map.on('draw.update', (e) => {
          this.broadcastUpdate()
        });
        this.map.on('draw.delete', (e) => {
          this.broadcastUpdate()
        });
        this.map.on('draw.selectionchange', () => {
          this.handleDrawSelectionChange();
        });

        this.map.on('draw.modechange', () => {
          this.handleDrawModeChange();
        });

        this.map.on('mousedown', (e) => {
          this.onMapMouseDown(e);
        });

        this.map.on('mousemove', (e) => {
          this.onMapMouseMove(e);
        });

        this.map.on('mouseup', (e) => {
          this.onMapMouseUp(e);
        });

        this.map.on('click', (e) => {
          this.onMapClick(e);
        });
      }

      this.map.on('moveend', () => {
        if (this.map.getZoom() < 13) {
          this.switchLayers('satellite-esri')
          if (this.showOtherBoundaries) {
            this.getBoundaries()
            this.showZoomTooltip = false
          }
        } else {
          this.switchLayers()
          this.showZoomTooltip = true
        }
      });
    },

    /**
     * Adds data to map and sets bounds.
     */
    postMapRender() {
      if (this.geography != null) {
        if (this.geography.type === 'FeatureCollection') {
          this.geography.features.forEach((element) => {
            this.addGeography(element);
          })
        } else {
          this.addGeography(this.geography);
        }

        this.setMapBoundsToFit(this.geography);
      }
    },

    /**
     * Adds map controls
     */
    addControls() {
      /* Given a query in the form "lng, lat" or "lat, lng"
        * returns the matching geographic coordinate(s)
        * as search results in carmen geojson format,
        * https://github.com/mapbox/carmen/blob/master/carmen-geojson.md */
      const coordinatesGeocoder = function (query) {
        // Match anything which looks like
        // decimal degrees coordinate pair.
        const matches = query.match(
            /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i
        );
        if (!matches) {
          return null;
        }

        function coordinateFeature(lng, lat) {
          return {
            center: [lng, lat],
            geometry: {
              type: 'Point',
              coordinates: [lng, lat]
            },
            place_name: 'Lat: ' + lat + ' Lng: ' + lng,
            place_type: ['coordinate'],
            properties: {},
            type: 'Feature'
          };
        }

        const coord1 = Number(matches[1]);
        const coord2 = Number(matches[2]);
        const geocodes = [];

        if (coord1 < -90 || coord1 > 90) {
          // must be lng, lat
          geocodes.push(coordinateFeature(coord1, coord2));
        }

        if (coord2 < -90 || coord2 > 90) {
          // must be lat, lng
          geocodes.push(coordinateFeature(coord2, coord1));
        }

        if (geocodes.length === 0) {
          // else could be either lng, lat or lat, lng
          geocodes.push(coordinateFeature(coord1, coord2));
          geocodes.push(coordinateFeature(coord2, coord1));
        }

        return geocodes;
      };

      let geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        localGeocoder: coordinatesGeocoder,
        zoom: 10,
        placeholder: 'Address or Coordinates',
        mapboxgl: mapboxgl,
        reverseGeocode: true,
        countries: 'usa'
      })

      this.map.addControl(geocoder, 'top-right');

      const initialZipCode = String(this.zipCode ?? '').trim();

      if (initialZipCode.length > 0) {
        geocoder.query(initialZipCode);
      }

      // Add zoom and rotation controls to the map.
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');

      if (this.draw) {
        this.map.addControl(this.mapDraw, 'top-left');
      }

      this.map.addControl(new mapboxgl.AttributionControl({
        customAttribution: 'NAIP Imagery courtesy of the U.S. Geological Survey & USDA',
        compact: true
      }));
    },

    /**
     * Reads an inputted geography
     */
    processGeography(geography) {
      if (geography) {
        this.geography = turf.getGeom(geography)
        this.center = this.calculateCenter(this.geography)
      } else {
        this.geography = null
        this.center = [-93.627594, 42.025494]
      }
    },

    /**
     * Returns geography as a feature collection. (For external use)
     */
    getGeography() {
      let data = turf.featureCollection(this.mapDraw.getAll().features);

      if (data.features.length > 0) {
        this.geography = data
      } else {
        this.geography = null
      }

      return {
        'geography': this.geography,
      };
    },

    /**
     * Computes the center of a geometry
     */
    calculateCenter(geometry) {
      return turf.getGeom(turf.center(geometry)).coordinates
    },

    /**
     * Add geography layer on the map.
     */
    addGeography(geography) {
      if (this.draw) {
        if (geography.type === "GeometryCollection") {
          // GeometryCollections must be converted. Does this work well? Not so sure...
          const polygons = [];

          turf.geomEach(geography, (geometry) => {
            if (geometry.type === "Polygon") {
              polygons.push(geometry)
            }
          });

          geography = {
            "type": "MultiPolygon",
            "coordinates": [[polygons]],
          }
        }
        this.mapDraw.add(geography)
      } else {
        let newSourceId = Math.random().toString(36).substring(2, 15);
        this.sources.push(newSourceId);

        this.map.addSource(newSourceId, {
          'type': 'geojson',
          'data': geography,
        });

        this.map.addLayer({
          'id': newSourceId,
          'type': 'fill', // options :'fill', "line", "symbol", "circle", "heatmap", "fill-extrusion", "raster", "hillshade", "background", "sky"... SET paint accordingly
          'source': newSourceId,
          'paint': {
            'fill-color': '#008ED3',
            'fill-opacity': 0.5
          }
        });

        if (this.outline) {
          this.map.addLayer({
            id: newSourceId + '-outline',
            type: 'line',
            source: newSourceId,
            paint: {
              "line-color": '#ffffff',
              'line-width': 2,
            }
          })
        }

        if (this.popups) {
          const popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false
          });

          this.map.on('mouseenter', newSourceId, (e) => {
            // Change the cursor style as a UI indicator.
            this.map.getCanvas().style.cursor = 'pointer';

            const center = turf.centerOfMass(e.features[0].geometry).geometry.coordinates
            const label = e.features[0].properties.name ?? this.sourceName;

            popup.setLngLat(center).setHTML(label).addTo(this.map);
          });

          this.map.on('mouseleave', newSourceId, () => {
            this.map.getCanvas().style.cursor = '';
            popup.remove();
          });

          this.map.on('click', newSourceId, (e) => {
            this.$emit('showField', 'field', e.features[0].properties.id)
          });
        }
      }
    },

    /**
     * Remove all geography layers on the map.
     */
    clearMap() {
      if (this.draw) {
        this.mapDraw.deleteAll()
      } else {
        this.sources.forEach((source) => {
          if (this.map.isSourceLoaded(source)) {
            this.map.removeLayer(source)
            this.map.removeSource(source)
          }
        })
      }
    },

    /**
     * Sets the bounds of a map to fit the given geography.
     */
    setMapBoundsToFit(geography) {
      this.fullBounds = new mapboxgl.LngLatBounds();
      let bbox = turf.bbox(geography);

      this.fullBounds.extend([bbox[0], bbox[1]]);
      this.fullBounds.extend([bbox[2], bbox[3]]);

      this.map.resize();
      this.map.fitBounds(this.fullBounds, {
        padding: 10,
        duration: 0,
      });
    },

    /**
     * broadcasts the current data.
     */
    broadcastUpdate() {
      this.$emit('updateGeography', {
        'geography': this.geography,
        'center': this.map.getCenter(),
        'type': 'mapbox',
      });
    },

    getBoundaries() {
      const canvas = this.map.getCanvas()
      const w = canvas.width
      const h = canvas.height
      const cUR = this.map.unproject([w * 0.9, 0]).toArray()
      const cLL = this.map.unproject([0, h * 0.9]).toArray()

      const bbox = cLL[0] + ', ' + cLL[1] + ', ' + cUR[0] + ', ' + cUR[1];

      this.get('/boundaries?bbox=' + bbox + '&excludeFarm=' + this.farmId)
        .then((response) => {
          if (this.map.getLayer('boundaries') !== undefined) {
            this.map.removeLayer('boundaries')
            this.map.removeLayer('boundaries-fill')
            this.map.removeSource('boundaries')
          }

          this.addBoundaries(response.data.boundaries);
        })
    },

    addBoundaries(geography) {
      this.map.addSource('boundaries', {
        'type': 'geojson',
        'data': geography,
        'generateId': true,
      });

      this.map.addLayer({
        'id': 'boundaries',
        'type': 'line', // options :'fill', "line", "symbol", "circle", "heatmap", "fill-extrusion", "raster", "hillshade", "background", "sky"... SET paint accordingly
        'source': 'boundaries',
        'paint': {
          'line-color': '#2c2c2c',
          'line-width': 2
        }
      });

      this.map.addLayer({
        'id': 'boundaries-fill',
        'type': 'fill', // options :'fill', "line", "symbol", "circle", "heatmap", "fill-extrusion", "raster", "hillshade", "background", "sky"... SET paint accordingly
        'source': 'boundaries',
        'paint': {
          'fill-color': '#939597',
          'fill-opacity': 0.75
        }
      });
    },

    toggleSettings() {
      this.showSettings = !this.showSettings;
    },

    switchLayers(defaultLayer) {
      // Hide layers
      for (let naip of this.naipLayers) {
        this.map.setLayoutProperty(naip, 'visibility', 'none')
      }
      // Show the one selected
      if (defaultLayer) {
        this.map.setLayoutProperty(defaultLayer, 'visibility', 'visible')
      } else {
        this.map.setLayoutProperty(this.rasterLayer, 'visibility', 'visible')
      }
    },

    toggleOtherBoundaries() {
      this.map.setLayoutProperty('boundaries-fill', 'visibility', this.showOtherBoundaries ? 'visible' : 'none')
      this.map.setLayoutProperty('boundaries', 'visibility', this.showOtherBoundaries ? 'visible' : 'none')

      if (this.showOtherBoundaries) {
        this.getBoundaries()
      }
    },

    switchMap() {
      this.broadcastUpdate();
    },

    getNaipLayers() {
      this.get('/naip-layers').then((response) => {
        this.naipLayers = response.data.layers;
        this.rasterLayer = response.data.defaultLayer;
      })
    },

    resetCustomEditModes() {
      this.multiVertexMode = false;
      this.clearSelectedVertices();
      this.showToolbarHelp = false;
      this.setToolbarNotice('');
      this.syncMultiVertexCursor();
    },

    activateDrawPolygonMode() {
      if (!this.draw || !this.mapDraw) {
        return;
      }

      this.resetCustomEditModes();
      this.mapDraw.changeMode('draw_polygon');
      this.currentDrawMode = 'draw_polygon';
    },

    combineSelectedFeatures() {
      if (!this.draw || !this.mapDraw) {
        return;
      }

      this.resetCustomEditModes();
      this.mapDraw.combineFeatures();
      this.currentDrawMode = this.mapDraw.getMode();
    },

    uncombineSelectedFeatures() {
      if (!this.draw || !this.mapDraw) {
        return;
      }

      this.resetCustomEditModes();
      this.mapDraw.uncombineFeatures();
      this.currentDrawMode = this.mapDraw.getMode();
    },

    trashSelectedFeature() {
      if (!this.draw || !this.mapDraw) {
        return;
      }

      if (this.multiVertexMode && this.selectedVertexPaths.length > 0) {
        this.deleteSelectedVertices();
        return;
      }

      this.resetCustomEditModes();
      this.mapDraw.trash();
      this.currentDrawMode = this.mapDraw.getMode();
    },

    setToolbarNotice(message = '') {
      this.toolbarNotice = message;
    },

    isUsableMultiVertexFeature(feature) {
      return feature?.geometry?.type === 'Polygon';
    },

    getFeatureForMultiVertex(featureId = null) {
      let resolvedFeatureId =
          featureId ||
          this.currentEditableFeatureId ||
          this.getCurrentEditableFeatureId();

      if (!resolvedFeatureId) {
        const all = this.mapDraw.getAll()?.features ?? [];
        if (all.length === 1 && all[0]?.id) {
          resolvedFeatureId = all[0].id;
        }
      }

      if (!resolvedFeatureId) {
        return null;
      }

      const feature = this.mapDraw.get(resolvedFeatureId);
      if (!feature) {
        return null;
      }

      return feature;
    },

    syncMultiVertexCursor() {
      this.updateMultiVertexHoverCursor();
    },

    toggleMultiVertexMode() {
      this.multiVertexMode = !this.multiVertexMode;

      if (!this.multiVertexMode) {
        this.setToolbarNotice('');
        this.clearSelectedVertices();
        this.syncMultiVertexCursor();
        return;
      }

      const feature = this.getFeatureForMultiVertex();

      if (!feature) {
        this.multiVertexMode = false;
        this.setToolbarNotice('Select a polygon first, then use Multi-Select Vertices.');
        this.syncMultiVertexCursor();
        return;
      }

      if (!this.isUsableMultiVertexFeature(feature)) {
        this.multiVertexMode = false;
        this.setToolbarNotice('Uncombine features before using Multi-Select Vertices.');
        this.syncMultiVertexCursor();
        return;
      }

      this.currentEditableFeatureId = feature.id;
      this.setToolbarNotice('');
      this.showToolbarHelp = false;
      this.syncMultiVertexCursor();

      this.mapDraw.changeMode('direct_select', { featureId: feature.id });
    },

    activateSplitMode() {
      if (!this.draw || !this.mapDraw) {
        return;
      }

      this.multiVertexMode = false;
      this.clearSelectedVertices();
      this.setToolbarNotice('');
      this.showToolbarHelp = false;
      this.syncMultiVertexCursor();

      this.mapDraw.changeMode('split_polygon', {
        highlightColor: '#222',
        lineWidth: 0,
        lineWidthUnit: 'kilometers',
      });
    },

    setCanvasCursor(cursor = '') {
      if (!this.map) {
        return;
      }

      this.map.getCanvas().style.cursor = cursor;
    },

    updateMultiVertexHoverCursor(point = null) {
      if (!this.map) {
        return;
      }

      if (!this.multiVertexMode) {
        this.setCanvasCursor('');
        return;
      }

      if (this.draggingSelectedVertices) {
        this.setCanvasCursor('grabbing');
        return;
      }

      const feature = this.getFeatureForMultiVertex();
      if (!feature || !this.isUsableMultiVertexFeature(feature)) {
        this.setCanvasCursor('crosshair');
        return;
      }

      if (!point) {
        this.setCanvasCursor('crosshair');
        return;
      }

      const vertex = this.getVertexFeatureAtPoint(point, feature.id);

      if (vertex) {
        const coordPath = String(vertex.properties.coord_path);

        if (this.selectedVertexPaths.includes(coordPath)) {
          this.setCanvasCursor('grab');
          return;
        }
      }

      this.setCanvasCursor('crosshair');
    },

    activateDirectSelectIfPossible() {
      if (!this.draw) {
        return;
      }

      const feature = this.getFeatureForMultiVertex();

      if (!feature) {
        this.setToolbarNotice('Select a polygon first, then use Multi-Select Vertices.');
        return;
      }

      if (!this.isUsableMultiVertexFeature(feature)) {
        this.setToolbarNotice('Uncombine features before using Multi-Select Vertices.');
        return;
      }

      this.currentEditableFeatureId = feature.id;
      this.setToolbarNotice('');

      if (this.mapDraw.getMode() !== 'direct_select') {
        this.mapDraw.changeMode('direct_select', { featureId: feature.id });
      }
    },

    getCurrentEditableFeatureId() {
      const ids = this.mapDraw.getSelectedIds ? this.mapDraw.getSelectedIds() : [];
      return ids?.length ? ids[0] : null;
    },

    handleDrawSelectionChange() {
      const nextId = this.getCurrentEditableFeatureId();

      if (!this.multiVertexMode) {
        if (nextId !== this.currentEditableFeatureId) {
          this.currentEditableFeatureId = nextId;
          this.clearSelectedVertices();
        }
        return;
      }

      // While Multi-Select Vertices is active, do not let blank-space clicks
      // clear the current editable feature.
      if (!nextId) {
        return;
      }

      if (nextId !== this.currentEditableFeatureId) {
        this.currentEditableFeatureId = nextId;
        this.clearSelectedVertices();
      }

      const feature = this.mapDraw.get(nextId);
      if (!feature) {
        return;
      }

      if (!this.isUsableMultiVertexFeature(feature)) {
        this.multiVertexMode = false;
        this.clearSelectedVertices();
        this.setToolbarNotice('Uncombine features before using Multi-Select Vertices.');
        this.syncMultiVertexCursor();
        return;
      }

      this.setToolbarNotice('');

      if (this.mapDraw.getMode() !== 'direct_select') {
        this.mapDraw.changeMode('direct_select', { featureId: nextId });
      }
    },

    handleDrawModeChange() {
      this.currentDrawMode = this.mapDraw?.getMode?.() ?? '';

      if (!this.multiVertexMode) {
        if (this.mapDraw.getMode() !== 'direct_select') {
          this.clearSelectedVertices();
        }

        this.stripMapboxControlTitles();
        return;
      }

      const feature = this.getFeatureForMultiVertex();

      if (!feature) {
        this.setToolbarNotice('Select a polygon first, then use Multi-Select Vertices.');
        this.stripMapboxControlTitles();
        return;
      }

      if (!this.isUsableMultiVertexFeature(feature)) {
        this.multiVertexMode = false;
        this.clearSelectedVertices();
        this.setToolbarNotice('Uncombine features before using Multi-Select Vertices.');
        this.syncMultiVertexCursor();
        this.stripMapboxControlTitles();
        return;
      }

      this.currentEditableFeatureId = feature.id;
      this.setToolbarNotice('');
      this.syncMultiVertexCursor();

      if (this.mapDraw.getMode() !== 'direct_select') {
        this.mapDraw.changeMode('direct_select', { featureId: feature.id });
      }

      this.stripMapboxControlTitles();
    },

    onGlobalKeyDown(event) {
      if (!this.multiVertexMode || this.selectedVertexPaths.length === 0) {
        return;
      }

      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        this.deleteSelectedVertices();
      }
    },

    onMapClick(e) {
      if (!this.draw || !this.multiVertexMode) {
        return;
      }

      if (this.suppressNextMapClick) {
        this.suppressNextMapClick = false;
        return;
      }

      const feature = this.getFeatureForMultiVertex();
      if (!feature) {
        return;
      }

      const featureId = feature.id;

      if (e.originalEvent?.shiftKey) {
        if (this.mapDraw.getMode() !== 'direct_select') {
          this.mapDraw.changeMode('direct_select', { featureId });
        }

        const vertex = this.getVertexFeatureAtPoint(e.point, featureId);
        if (!vertex) {
          return;
        }

        const coordPath = String(vertex.properties.coord_path);
        this.toggleSelectedVertex(coordPath);
        return;
      }

      // If clicking blank map space while the tool is active,
      // keep the current editable polygon instead of letting Draw reset it.
      const vertex = this.getVertexFeatureAtPoint(e.point, featureId);
      if (!vertex && this.mapDraw.getMode() !== 'direct_select') {
        this.mapDraw.changeMode('direct_select', { featureId });
      }
    },

    onMapMouseDown(e) {
      if (!this.draw || !this.multiVertexMode) {
        return;
      }

      const feature = this.getFeatureForMultiVertex();
      if (!feature) {
        this.setToolbarNotice('Select a polygon first, then use Multi-Select Vertices.');
        return;
      }

      if (!this.isUsableMultiVertexFeature(feature)) {
        this.multiVertexMode = false;
        this.clearSelectedVertices();
        this.setToolbarNotice('Uncombine features before using Multi-Select Vertices.');
        this.syncMultiVertexCursor();
        return;
      }

      const featureId = feature.id;
      this.currentEditableFeatureId = featureId;

      if (this.mapDraw.getMode() !== 'direct_select') {
        this.mapDraw.changeMode('direct_select', { featureId });
      }

      const vertex = this.getVertexFeatureAtPoint(e.point, featureId);

      if (vertex) {
        const coordPath = String(vertex.properties.coord_path);

        if (this.selectedVertexPaths.includes(coordPath)) {
          this.draggingSelectedVertices = true;
          this.dragFeatureId = featureId;
          this.dragLastLngLat = [e.lngLat.lng, e.lngLat.lat];
          this.map.dragPan.disable();
          this.setCanvasCursor('grabbing');
          e.originalEvent?.preventDefault?.();
          return;
        }
      }

      this.mapDraw.changeMode('direct_select', { featureId });
      this.clearSelectedVertices();

      this.selectionBox.active = true;
      this.selectionBox.start = { x: e.point.x, y: e.point.y };
      this.selectionBox.current = { x: e.point.x, y: e.point.y };

      this.map.dragPan.disable();
      e.originalEvent?.preventDefault?.();
    },

    onMapMouseMove(e) {
      this.updateMultiVertexHoverCursor(e.point);

      if (!this.draw || !this.multiVertexMode) {
        return;
      }

      if (this.draggingSelectedVertices && this.dragFeatureId) {
        const next = [e.lngLat.lng, e.lngLat.lat];
        const prev = this.dragLastLngLat;

        const dx = next[0] - prev[0];
        const dy = next[1] - prev[1];

        if (dx === 0 && dy === 0) {
          return;
        }

        this.moveSelectedVertices(dx, dy);
        this.dragLastLngLat = next;
        return;
      }

      if (this.selectionBox.active) {
        this.selectionBox.current = { x: e.point.x, y: e.point.y };
      }
    },

    onMapMouseUp() {
      if (!this.draw || !this.multiVertexMode) {
        return;
      }

      if (this.draggingSelectedVertices) {
        this.draggingSelectedVertices = false;
        this.dragFeatureId = null;
        this.dragLastLngLat = null;
        this.map.dragPan.enable();
        this.broadcastUpdate();

        const featureId = this.getCurrentEditableFeatureId() || this.currentEditableFeatureId;
        if (featureId) {
          this.mapDraw.changeMode('direct_select', { featureId });
        }

        this.refreshSelectedVertexOverlay();
        this.updateMultiVertexHoverCursor();
        return;
      }

      if (!this.selectionBox.active) {
        return;
      }

      const feature = this.getFeatureForMultiVertex();

      this.selectionBox.active = false;
      this.map.dragPan.enable();

      if (!feature) {
        this.selectionBox.start = null;
        this.selectionBox.current = null;
        return;
      }

      const featureId = feature.id;

      const start = this.selectionBox.start;
      const end = this.selectionBox.current;

      const minX = Math.min(start.x, end.x);
      const minY = Math.min(start.y, end.y);
      const maxX = Math.max(start.x, end.x);
      const maxY = Math.max(start.y, end.y);

      const width = Math.abs(maxX - minX);
      const height = Math.abs(maxY - minY);

      if (width < 4 && height < 4) {
        this.selectionBox.start = null;
        this.selectionBox.current = null;
        this.updateMultiVertexHoverCursor();
        return;
      }

      const features = this.map.queryRenderedFeatures([[minX, minY], [maxX, maxY]]);
      const vertexPaths = features
          .filter((feature) =>
              feature?.properties?.meta === 'vertex' &&
              String(feature?.properties?.parent) === String(featureId)
          )
          .map((feature) => String(feature.properties.coord_path));

      this.selectedVertexPaths = [...new Set(vertexPaths)];
      this.refreshSelectedVertexOverlay();
      this.suppressNextMapClick = true;

      this.selectionBox.start = null;
      this.selectionBox.current = null;
      this.updateMultiVertexHoverCursor();
    },

    getVertexFeatureAtPoint(point, featureId = null) {
      const features = this.map.queryRenderedFeatures(point);

      return features.find((feature) => {
        if (feature?.properties?.meta !== 'vertex') {
          return false;
        }

        if (!featureId) {
          return true;
        }

        return String(feature.properties.parent) === String(featureId);
      }) || null;
    },

    toggleSelectedVertex(coordPath) {
      if (this.selectedVertexPaths.includes(coordPath)) {
        this.selectedVertexPaths = this.selectedVertexPaths.filter((path) => path !== coordPath);
      } else {
        this.selectedVertexPaths = [...this.selectedVertexPaths, coordPath];
      }

      this.refreshSelectedVertexOverlay();
    },

    clearSelectedVertices() {
      this.selectedVertexPaths = [];
      this.refreshSelectedVertexOverlay();
    },

    ensureSelectedVertexOverlay() {
      if (!this.map.getSource('selected-vertices')) {
        this.map.addSource('selected-vertices', {
          type: 'geojson',
          data: turf.featureCollection([]),
        });
      }

      if (!this.map.getLayer('selected-vertices-halo')) {
        this.map.addLayer({
          id: 'selected-vertices-halo',
          type: 'circle',
          source: 'selected-vertices',
          paint: {
            'circle-radius': 8,
            'circle-color': '#ffffff',
          },
        });
      }

      if (!this.map.getLayer('selected-vertices-fill')) {
        this.map.addLayer({
          id: 'selected-vertices-fill',
          type: 'circle',
          source: 'selected-vertices',
          paint: {
            'circle-radius': 5,
            'circle-color': '#ec4899',
          },
        });
      }
    },

    refreshSelectedVertexOverlay() {
      if (!this.map || !this.map.getSource('selected-vertices')) {
        return;
      }

      const featureId = this.getCurrentEditableFeatureId() || this.currentEditableFeatureId;
      if (!featureId || this.selectedVertexPaths.length === 0) {
        this.map.getSource('selected-vertices').setData(turf.featureCollection([]));
        return;
      }

      const feature = this.mapDraw.get(featureId);
      if (!feature) {
        this.map.getSource('selected-vertices').setData(turf.featureCollection([]));
        return;
      }

      const points = this.selectedVertexPaths
          .map((path) => this.getCoordinateForPath(feature.geometry, path))
          .filter(Boolean)
          .map((coordinates) => turf.point(coordinates));

      this.map.getSource('selected-vertices').setData(turf.featureCollection(points));
    },

    getCoordinateForPath(geometry, coordPath) {
      const parts = String(coordPath).split('.').map((part) => parseInt(part, 10));

      if (geometry.type === 'Polygon' && parts.length === 2) {
        return geometry.coordinates?.[parts[0]]?.[parts[1]] ?? null;
      }

      if (geometry.type === 'MultiPolygon' && parts.length === 3) {
        return geometry.coordinates?.[parts[0]]?.[parts[1]]?.[parts[2]] ?? null;
      }

      return null;
    },

    setCoordinateForPath(geometry, coordPath, nextCoordinate) {
      const parts = String(coordPath).split('.').map((part) => parseInt(part, 10));

      let ring = null;
      let vertexIndex = null;

      if (geometry.type === 'Polygon' && parts.length === 2) {
        ring = geometry.coordinates?.[parts[0]];
        vertexIndex = parts[1];
      } else if (geometry.type === 'MultiPolygon' && parts.length === 3) {
        ring = geometry.coordinates?.[parts[0]]?.[parts[1]];
        vertexIndex = parts[2];
      }

      if (!ring || vertexIndex === null || !ring[vertexIndex]) {
        return;
      }

      ring[vertexIndex] = [...nextCoordinate];

      if (vertexIndex === 0) {
        ring[ring.length - 1] = [...nextCoordinate];
      } else if (vertexIndex === ring.length - 1) {
        ring[0] = [...nextCoordinate];
      }
    },

    moveSelectedVertices(deltaLng, deltaLat) {
      const featureId = this.dragFeatureId || this.getCurrentEditableFeatureId();
      if (!featureId) {
        return;
      }

      const feature = this.mapDraw.get(featureId);
      if (!feature) {
        return;
      }

      const nextFeature = JSON.parse(JSON.stringify(feature));

      this.selectedVertexPaths.forEach((path) => {
        const current = this.getCoordinateForPath(nextFeature.geometry, path);
        if (!current) {
          return;
        }

        this.setCoordinateForPath(nextFeature.geometry, path, [
          current[0] + deltaLng,
          current[1] + deltaLat,
        ]);
      });

      this.mapDraw.add(nextFeature);
      this.currentEditableFeatureId = featureId;
      this.refreshSelectedVertexOverlay();
    },

    deleteSelectedVertices() {
      const feature = this.getFeatureForMultiVertex();
      if (!feature || this.selectedVertexPaths.length === 0) {
        return;
      }

      const featureId = feature.id;
      const nextFeature = JSON.parse(JSON.stringify(feature));
      const geometry = nextFeature.geometry;

      const groupedSelections = {};

      this.selectedVertexPaths.forEach((coordPath) => {
        const parts = String(coordPath).split('.').map((part) => parseInt(part, 10));

        let groupKey = null;
        let ring = null;
        let vertexIndex = null;

        if (geometry.type === 'Polygon' && parts.length === 2) {
          groupKey = `polygon:${parts[0]}`;
          ring = geometry.coordinates?.[parts[0]];
          vertexIndex = parts[1];
        } else if (geometry.type === 'MultiPolygon' && parts.length === 3) {
          groupKey = `multipolygon:${parts[0]}:${parts[1]}`;
          ring = geometry.coordinates?.[parts[0]]?.[parts[1]];
          vertexIndex = parts[2];
        }

        if (!groupKey || !ring || vertexIndex === null) {
          return;
        }

        if (!groupedSelections[groupKey]) {
          groupedSelections[groupKey] = {
            ring,
            indexes: [],
          };
        }

        groupedSelections[groupKey].indexes.push(vertexIndex);
      });

      let shouldDeleteWholeFeature = false;

      Object.values(groupedSelections).forEach((group) => {
        if (shouldDeleteWholeFeature) {
          return;
        }

        const ring = group.ring;
        if (!Array.isArray(ring) || ring.length < 4) {
          shouldDeleteWholeFeature = true;
          return;
        }

        // Remove closing duplicate for logic
        const openRing = ring.slice(0, -1);

        // Normalize closing coordinate index back to index 0, then dedupe
        const normalizedIndexes = [...new Set(
            group.indexes.map((index) => (index === ring.length - 1 ? 0 : index))
        )].sort((a, b) => a - b);

        const remainingOpenRing = openRing.filter((_, index) => !normalizedIndexes.includes(index));

        // If fewer than 3 unique vertices remain, polygon can no longer exist
        if (remainingOpenRing.length < 3) {
          shouldDeleteWholeFeature = true;
          return;
        }

        const closedRing = [...remainingOpenRing, [...remainingOpenRing[0]]];
        ring.splice(0, ring.length, ...closedRing);
      });

      if (shouldDeleteWholeFeature) {
        this.mapDraw.delete(featureId);
        this.currentEditableFeatureId = null;
        this.clearSelectedVertices();
        this.broadcastUpdate();
        this.updateMultiVertexHoverCursor();
        return;
      }

      this.mapDraw.add(nextFeature);
      this.currentEditableFeatureId = featureId;
      this.clearSelectedVertices();
      this.mapDraw.changeMode('direct_select', { featureId });
      this.broadcastUpdate();
      this.updateMultiVertexHoverCursor();
    },

    selectionBoxStyle() {
      if (!this.selectionBox.active || !this.selectionBox.start || !this.selectionBox.current) {
        return {};
      }

      const left = Math.min(this.selectionBox.start.x, this.selectionBox.current.x);
      const top = Math.min(this.selectionBox.start.y, this.selectionBox.current.y);
      const width = Math.abs(this.selectionBox.start.x - this.selectionBox.current.x);
      const height = Math.abs(this.selectionBox.start.y - this.selectionBox.current.y);

      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
      };
    },

    bindTrashOverride() {
      if (!this.$refs.mapShell) {
        return;
      }

      const trashButton = this.$refs.mapShell.querySelector('.mapbox-gl-draw_trash');
      if (!trashButton || trashButton.dataset.multiVertexBound === 'true') {
        return;
      }

      trashButton.dataset.multiVertexBound = 'true';

      trashButton.addEventListener('click', (event) => {
        if (!this.multiVertexMode || this.selectedVertexPaths.length === 0) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        this.deleteSelectedVertices();
      }, true);
    },

    stripMapboxControlTitles() {
      if (!this.$refs.mapShell) {
        return;
      }

      this.$refs.mapShell
          .querySelectorAll('.mapboxgl-ctrl button, .mapboxgl-ctrl input')
          .forEach((element) => {
            element.removeAttribute('title');
          });

      this.bindTrashOverride();
    },
  },
};

</script>

<template>
  <div ref="mapShell" class="w-full text-black">
    <div
        :id="this.mapId ?? this.identifier"
        class="relative w-full"
        :class="this.height ?? 'h-192'"
    >
      <div
          class="absolute left-4 top-4 z-40 flex flex-col items-start gap-2"
          style="left: 1rem; top: 1rem;"
      >
        <div class="flex flex-col items-stretch gap-2">

          <template v-if="draw">
            <!-- menu expand-->
            <button
                type="button"
                class="bg-white rounded shadow py-2 text-sm border border-grey-light text-black flex items-center"
                :class="toolbarExpanded ? 'w-44 px-3 text-left justify-start' : 'w-10 px-0 justify-center'"
                @click="toolbarExpanded = !toolbarExpanded"
                :aria-label="toolbarExpanded ? 'Hide menu' : 'Expand menu'"
            >
              <svg
                  class="text-center"
                  :class="toolbarExpanded ? 'mr-2' : ''"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
              >
                <path
                    :d="toolbarExpanded
                      ? 'M15 18L9 12L15 6'
                      : 'M9 18L15 12L9 6'"
                />
              </svg>

              <span v-if="toolbarExpanded">Hide Menu</span>
            </button>
            <!-- draw boundary-->
            <button
                type="button"
                class="bg-white rounded shadow py-2 gap-2 text-sm border border-grey-light text-black flex items-center"
                :class="[
                  toolbarExpanded ? 'w-44 px-3 text-left justify-start' : 'w-10 px-0 justify-center',
                  currentDrawMode === 'draw_polygon' ? 'border-blue text-blue' : ''
                ]"
                @click="activateDrawPolygonMode"
            >
              <i
                  class="fas fa-draw-polygon w-4 text-center"
                  :class="toolbarExpanded ? 'mr-2' : ''"
              ></i>
              <span v-if="toolbarExpanded">Draw Boundary</span>
            </button>
            <!-- Combine Features-->
            <button
                type="button"
                class="bg-white rounded shadow py-2 gap-2 text-sm border border-grey-light text-black flex items-center"
                :class="toolbarExpanded ? 'w-44 px-3 text-left justify-start' : 'w-10 px-0 justify-center'"
                @click="combineSelectedFeatures"
            >
              <i
                  class="fas fa-object-group w-4 text-center"
                  :class="toolbarExpanded ? 'mr-2' : ''"
              ></i>
              <span v-if="toolbarExpanded">Combine Features</span>
            </button>
            <!-- Uncombine Features-->
            <button
                type="button"
                class="bg-white rounded shadow py-2 gap-2 text-sm border border-grey-light text-black flex items-center"
                :class="toolbarExpanded ? 'w-44 px-3 text-left justify-start' : 'w-10 px-0 justify-center'"
                @click="uncombineSelectedFeatures"
            >
              <i
                  class="fas fa-object-ungroup w-4 text-center"
                  :class="toolbarExpanded ? 'mr-2' : ''"
              ></i>
              <span v-if="toolbarExpanded">Uncombine Features</span>
            </button>
            <!-- Split Polygon-->
            <button
                type="button"
                class="bg-white rounded shadow py-2 gap-2 text-sm border border-grey-light text-black flex items-center"
                :class="[
      toolbarExpanded ? 'w-44 px-3 text-left justify-start' : 'w-10 px-0 justify-center',
      currentDrawMode === 'split_polygon' ? 'border-blue text-blue' : ''
    ]"
                @click="activateSplitMode"
            >
              <i
                  class="fas fa-cut w-4 text-center"
                  :class="toolbarExpanded ? 'mr-2' : ''"
              ></i>
              <span v-if="toolbarExpanded">Split Polygon</span>
            </button>
            <!-- Multi-Select Vertices-->
            <button
                type="button"
                class="bg-white rounded shadow py-2 gap-2 text-sm border border-grey-light text-black flex items-center"
                :class="[
      toolbarExpanded ? 'w-44 px-3 text-left justify-start' : 'w-10 px-0 justify-center',
      multiVertexMode ? 'border-blue text-blue' : ''
    ]"
                @click="toggleMultiVertexMode"
            >
              <i
                  class="fas fa-mouse-pointer w-4 text-center"
                  :class="toolbarExpanded ? 'mr-2' : ''"
              ></i>
              <span v-if="toolbarExpanded">Multi-Select Vertices</span>
            </button>
            <!-- Delete Feature-->
            <button
                type="button"
                class="bg-white rounded shadow py-2 gap-2 text-sm border border-grey-light text-black flex items-center"
                :class="toolbarExpanded ? 'w-44 px-3 text-left justify-start' : 'w-10 px-0 justify-center'"
                @click="trashSelectedFeature"
            >
              <i
                  class="fas fa-trash w-4 text-center"
                  :class="toolbarExpanded ? 'mr-2' : ''"
              ></i>
              <span v-if="toolbarExpanded">Delete Feature</span>
            </button>
          </template>
        </div>
      </div>

      <div
          class="absolute left-4 bottom-4 z-40 flex flex-col items-start gap-2"
          :style="draw ? 'left: 1rem; bottom: 2rem;' : 'left: 1rem; bottom: 1rem;'"
      >
        <div
            v-show="showSettings"
            class="bg-white rounded shadow border border-grey-light p-3 w-64 text-sm"
        >
          <h1 class="mb-2 font-semibold">Settings</h1>

          <div class="border-b py-1">
            <label class="text-lg">NAIP Layer</label>
            <select
                class="w-full bg-secondary italic border-b border-grey-light px-2 py-1 rounded-md leading-tight focus:outline-none focus:ring"
                v-on:change="this.switchLayers()"
                v-model="this.rasterLayer"
            >
              <option v-if="this.naipLayers.includes('naip-usda')" :value="'naip-usda'">USDA</option>
              <option v-if="this.naipLayers.includes('naip-usgs')" :value="'naip-usgs'">USGS</option>
              <option v-if="this.naipLayers.includes('satellite-mapbox')" :value="'satellite-mapbox'">Mapbox</option>
              <option v-if="this.naipLayers.includes('satellite-esri')" :value="'satellite-esri'">ESRI</option>
            </select>
          </div>

          <div class="my-1">
            <label class="text-lg">Show Other Boundaries</label>
            <input
                type="checkbox"
                class="block"
                v-model="this.showOtherBoundaries"
                v-on:change="toggleOtherBoundaries()"
            >
          </div>
        </div>

        <div
            v-if="showToolbarHelp"
            class="bg-white rounded shadow border border-grey-light p-3 w-80 text-sm"
        >
          <div class="font-semibold mb-3">{{ helpTitle }}</div>

          <div class="space-y-3">
            <div
                v-for="(section, sectionIndex) in toolbarHelpSections"
                :key="sectionIndex"
            >
              <div class="flex items-center font-semibold mb-1">
                <i :class="section.icon" class="mr-2 w-4 text-center"></i>
                <span>{{ section.title }}</span>
              </div>

              <ul class="list-disc pl-6 space-y-1 text-xs leading-snug">
                <li
                    v-for="(item, itemIndex) in section.items"
                    :key="itemIndex"
                >
                  {{ item }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div
            v-if="toolbarNotice"
            class="rounded shadow p-3 w-80 text-sm font-semibold flex items-start justify-between"
            style="background-color: #fee2e2; color: #7f1d1d; border: 1px solid #fca5a5;"
        >
          <button
              type="button"
              class="ml-1 text-lg leading-none font-bold"
              style="color: #7f1d1d;"
              @click="setToolbarNotice('')"
              aria-label="Dismiss notice"
          >
            &times;
          </button>

          <div class="flex items-start">
            <i class="fas fa-exclamation-circle mx-2 mt-1"></i>
            <span>{{ toolbarNotice }}</span>
          </div>

        </div>

        <div class="flex items-center gap-2">
          <button
              type="button"
              class="bg-white rounded shadow h-10 w-10 px-0 text-sm border border-grey-light text-black flex items-center justify-center"
              @click="toggleSettings(); showToolbarHelp = false"
              aria-label="Map Settings"
          >
            <i class="fas fa-cog w-4 text-center"></i>
          </button>

          <button
              v-if="draw"
              type="button"
              class="bg-white rounded shadow h-10 text-sm border border-grey-light text-black flex items-center justify-center leading-none"
              :class="toolbarExpanded ? 'px-3 gap-2' : 'w-10 px-0'"
              @click="showToolbarHelp = !showToolbarHelp; showSettings = false"
              aria-label="Editing Help"
          >
            <i class="fas fa-info-circle w-4 text-center"></i>
            <span v-if="toolbarExpanded">Help</span>
          </button>
        </div>
      </div>

      <div
          v-if="selectionBox.active"
          class="absolute z-20 border-2 border-blue bg-blue-lighter opacity-25 pointer-events-none"
          :style="selectionBoxStyle()"
      ></div>
    </div>
  </div>
</template>